import styled from '@emotion/styled';
import classNames from 'classnames';
import React, { HTMLAttributes, ComponentType, ReactNode } from 'react';
import { IconCircleInfo, IconColoredWarning, IconLoadingDonuts, IconProps } from '../../components/icons/generated';
import { Stack } from '../../components/stack';
import { BaseText } from '../../components/text';
import { Typography, semantic_colors } from '../../foundation';

export type StateStatus = 'warning' | 'empty' | 'loading';
export interface StateProps extends Omit<HTMLAttributes<HTMLDivElement>, 'title'> {
  status?: StateStatus;
  /**
   * default: 20
   */
  size?: 20 | 32 | 40;
  title?: ReactNode;
  description: ReactNode;
  icon?: ComponentType<IconProps>;
  action?: ReactNode;
}

const stateStatusIcon: { [key in StateStatus]: ComponentType } = {
  warning: IconColoredWarning,
  empty: IconCircleInfo,
  loading: IconLoadingDonuts,
} as const;

export const State = ({ status, size = 20, title, description, action, icon, className, ...props }: StateProps) => {
  const Icon = icon ?? (status ? (stateStatusIcon[status] as ComponentType<IconProps>) : undefined);
  const desc_typography: Typography = status === 'warning' ? 'Body_15_SemiBold' : 'Body_14_Medium';

  return (
    <Stack direction='column' gap={8} align='center' className={classNames('pds-state', className)} {...props}>
      {Icon && <Icon aria-hidden size={size} color={semantic_colors.content.tertiary} />}
      {title && (
        <StyledText typography='Heading_24_Bold' color={semantic_colors.content.primary}>
          {title}
        </StyledText>
      )}
      <Stack direction='column' gap={16} align='center'>
        {description && (
          <StyledText typography={desc_typography} color={semantic_colors.content.tertiary}>
            {description}
          </StyledText>
        )}
        {action && action}
      </Stack>
    </Stack>
  );
};

const StyledText = styled(BaseText)`
  white-space: pre-line;
  text-align: center;
`;
